import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { Draft, produce } from 'immer';
import { Form, FormInput } from '../types';
import { isObjectEmpty } from '@dh/utils';
import { StepDetails } from '@dh/components';

export type UpdateErrorOnCallback<TForm extends Form> = (
    field: keyof TForm,
    message?: string
) => void;

export type ValidationStepProps<TForm extends Form> = {
    updateErrorOn: UpdateErrorOnCallback<TForm>;
    data: TForm;
};

export type ValidationCallback<TForm extends Form> = (props: ValidationStepProps<TForm>) => void;

export type WizardStepProps<TForm extends Form> = {
    form: TForm;
    initForm: (field: keyof TForm, value: unknown) => void;
    updateForm: (field: keyof TForm, value: unknown) => void;
    updateErrorOn: UpdateErrorOnCallback<TForm>;
    updateOptionsOn: (field: keyof TForm, opts: { isMandatory: boolean }) => void;
    validateOn: () => void;
};

// Generic configuration type that allows us to access every field in each section
type FormData = Record<string, Form<Record<string, FormInput>>>;

type ValidateOn<TForm extends FormData> = {
    [K in keyof TForm]?: { [F in keyof TForm[K]]?: ValidationCallback<TForm[K]> };
};

export type StepConfig<T extends FormData> = {
    [K in keyof T]: {
        label: string;
        description: string;
        content: (prop: WizardStepProps<T[K]>) => React.ReactNode;
        hasErrors?: () => boolean;
    };
};

export const useWizard = <TForm extends FormData>({
    initialData,
    validations,
    steps,
    onChangePatch
}: {
    initialData: TForm;
    validations: ValidateOn<TForm>;
    steps: StepConfig<TForm>;
    onChangePatch?: <
        TSectionKey extends keyof TForm = keyof TForm,
        TFieldKey extends keyof TForm[TSectionKey] = keyof TForm[TSectionKey]
    >(
        change: {
            sectionKey: TSectionKey;
            fieldKey: TFieldKey;
            value: unknown;
        },
        draft: Draft<TForm>
    ) => void;
}) => {
    const [form, _updateForm] = useState<TForm>(initialData);

    const [formDirty, setFormDirty] = useState<(keyof TForm)[]>([]);
    const isDirty = formDirty.length > 0;
    const formDirtySections = useCallback(
        (form: TForm) => {
            // We use using the formDirty state to only return the dirty sections (different from initialData).
            // We MUST NOT use _changes, because those could be reset by user
            return formDirty.reduce((changedSections, sectionKey) => {
                return {
                    ...changedSections,
                    [sectionKey]: form[sectionKey]
                };
            }, {} as Partial<TForm>);
        },
        [formDirty]
    );

    // collect user changed fields
    const [_changes, _setChanges] = useState<{
        [KS in keyof TForm]?: { [KF in keyof TForm[KS]]?: boolean };
    }>({});
    const hasChanges = Object.keys(_changes).length > 0;
    const setFieldChanged = useCallback(
        <TSectionKey extends keyof TForm, TFieldKey extends keyof TForm[TSectionKey]>(
            sectionKey: TSectionKey,
            fieldKey: TFieldKey
        ) => {
            _setChanges((prev) => ({
                ...prev,
                [sectionKey]: {
                    ...prev[sectionKey],
                    [fieldKey]: true
                }
            }));
        },
        [_setChanges]
    );

    const updateErrorOn = useCallback(
        <TSectionKey extends keyof TForm, TFieldKey extends keyof TForm[TSectionKey]>(
            sectionKey: TSectionKey
        ) =>
            (fieldKey: TFieldKey, message?: string) =>
                _updateForm((state) => {
                    throwIfNotFound(state, sectionKey, fieldKey);
                    return produce(state, (draft: TForm) => {
                        draft[sectionKey][fieldKey].errorText = message;
                        draft[sectionKey][fieldKey].hasError = !!message;
                    });
                }),
        [_updateForm]
    );

    const updateOptionsOf = useCallback(
        <TSectionKey extends keyof TForm, TFieldKey extends keyof TForm[TSectionKey]>(
            sectionKey: TSectionKey
        ) =>
            (fieldKey: TFieldKey, opts: { isMandatory: boolean }) =>
                _updateForm((state) => {
                    throwIfNotFound(state, sectionKey, fieldKey);
                    return produce(state, (draft: TForm) => {
                        draft[sectionKey][fieldKey].isMandatory = opts.isMandatory;
                    });
                }),
        [_updateForm]
    );

    const updateFieldOf = useCallback(
        <TSectionKey extends keyof TForm, TFieldKey extends keyof TForm[TSectionKey]>(
            sectionKey: TSectionKey,
            mode: 'change' | 'init'
        ) => {
            return (fieldKey: TFieldKey, value: unknown) => {
                // check if value would be changed
                throwIfNotFound(form, sectionKey, fieldKey);
                if (form[sectionKey][fieldKey].value === value) {
                    return;
                }

                // we will record the dirty state only for changes
                if (mode === 'change') {
                    setFieldChanged(sectionKey, fieldKey);
                    setFormDirty((prevState) =>
                        !prevState.includes(sectionKey) ? [...prevState, sectionKey] : prevState
                    );
                }
                // update form value state
                _updateForm((state) =>
                    produce(state, (draft: TForm) => {
                        draft[sectionKey][fieldKey].value = value;

                        // side effect
                        onChangePatch?.({ sectionKey, fieldKey, value }, draft as Draft<TForm>);
                    })
                );
            };
        },
        [form, onChangePatch, setFieldChanged]
    );

    type ValidateOnResult<TForm extends FormData, TSectionKey extends keyof TForm = keyof TForm> = {
        [K in keyof TForm[TSectionKey]]?: { message: string };
    };

    const validateOn = useCallback(
        <TSectionKey extends keyof TForm>(
            sectionKey: TSectionKey,
            fieldKeys: (keyof TForm[TSectionKey])[],
            updateErrorOnCallback?: UpdateErrorOnCallback<TForm[TSectionKey]>
        ) => {
            return () => {
                // we will collect the found errors in the errors instance
                let errors: ValidateOnResult<TForm, TSectionKey> = {};
                const errorOnCallback = updateErrorOnCallback ?? updateErrorOn(sectionKey);
                const localCallback: UpdateErrorOnCallback<TForm[TSectionKey]> = (
                    fieldKey,
                    message
                ) => {
                    errorOnCallback(fieldKey, message);
                    if (!!message) {
                        errors = { ...errors, [fieldKey]: { message } };
                    }
                };
                // for each validation defined in the section, validate the given fields
                const sectionValidations = validations[sectionKey];
                if (sectionValidations) {
                    fieldKeys.forEach((fieldKey) => {
                        const v = sectionValidations[fieldKey];
                        v?.({
                            updateErrorOn: localCallback,
                            data: form[sectionKey]
                        });
                    });
                }
                return errors;
            };
        },
        [form, updateErrorOn, validations]
    );

    type ValidateFormResult<TForm extends FormData> = {
        [K in keyof TForm]?: ValidateOnResult<TForm, K>;
    };
    const _validateForm = useCallback(
        (changes?: typeof _changes) => {
            let errors: ValidateFormResult<TForm> = {};
            Object.keys(validations).forEach((sectionKey) => {
                const sk = sectionKey as keyof TForm;
                const fieldKeys = changes !== undefined ? Object.keys(changes[sk] ?? {}) : [];
                if (fieldKeys.length === 0) return;
                const result = validateOn(sk, fieldKeys)();
                if (Object.keys(result).length > 0) errors = { ...errors, [sk]: result };
            });
            return {
                isValid: Object.keys(errors).length === 0,
                errors: errors
            };
        },
        [validateOn, validations]
    );
    // NOTE: mandatory validation is called synchronously to prevent re-rendering
    useLayoutEffect(() => {
        // on implicit validation we validate only changes
        _validateForm(_changes);
    });
    const validateForm = () => {
        // but on explicit validation we have to validate all fields of the form
        const allFieldsOfAllSections = Object.keys(form).reduce(
            (prev, sk) => ({
                ...prev,
                [sk]: Object.keys(form[sk]).reduce((prev, fk) => ({ ...prev, [fk]: true }), {})
            }),
            {}
        );
        return _validateForm(allFieldsOfAllSections);
    };

    const updateForm = useCallback(
        <TSectionKey extends keyof TForm>(
            sectionKey: TSectionKey,
            fieldKey: keyof TForm[TSectionKey],
            value: unknown
        ) => {
            updateFieldOf(sectionKey, 'change')(fieldKey, value);
        },
        [updateFieldOf]
    );

    const stepProps = useCallback(
        <TSectionKey extends keyof TForm>(
            sectionKey: TSectionKey
        ): WizardStepProps<TForm[TSectionKey]> => {
            const errorOn = updateErrorOn(sectionKey);
            return {
                form: form[sectionKey],
                initForm: updateFieldOf(sectionKey, 'init'),
                updateForm: updateFieldOf(sectionKey, 'change'),
                updateOptionsOn: updateOptionsOf(sectionKey),
                updateErrorOn: errorOn,
                validateOn: validateOn(sectionKey, Object.keys(form[sectionKey]), errorOn)
            };
        },
        [form, updateErrorOn, updateFieldOf, updateOptionsOf, validateOn]
    );

    const hasErrorsOn = useCallback(
        <TSectionKey extends keyof TForm>(sectionKey: TSectionKey) =>
            () => {
                const fields = Object.values<FormInput>(form[sectionKey]);
                return fields.some(
                    (field) => field.hasError || (field.isMandatory && isObjectEmpty(field.value))
                );
            },
        [form]
    );

    const usedSteps = useMemo<StepDetails[]>(
        () =>
            Object.entries(steps).map(([key, value]) => ({
                ...value,
                content: value.content(stepProps(key as keyof TForm) as any),
                hasErrors: hasErrorsOn(key as keyof TForm)(),
                key
            })),
        [steps, stepProps, hasErrorsOn]
    );

    const [activeStep, setActiveStep] = useState(0);
    const goToStep = (step: number) => {
        setActiveStep(step);
    };

    return {
        form,
        formDirtySections,
        isDirty,
        updateForm,
        validateForm,
        resetForm,
        resetChanges,
        hasChanges,
        stepper: {
            showStepIcons: false,
            steps: usedSteps,
            activeStep,
            onChangeStep: goToStep
        }
    };

    function resetChanges() {
        return _setChanges({});
    }
    function resetForm() {
        resetChanges();
        setFormDirty([]);
    }
};

// Helper function to throw an error if a field in section or section is not found
function throwIfNotFound<
    TForm extends FormData,
    TSectionKey extends keyof TForm,
    TFieldKey extends keyof TForm[TSectionKey]
>(state: TForm, sectionKey: TSectionKey, fieldKey: TFieldKey) {
    if (!state[sectionKey] || !state[sectionKey][fieldKey]) {
        throw new Error(
            `Invalid section '${sectionKey.toString()}' or field '${fieldKey.toString()}'`,
            {
                cause: { sectionKey, fieldKey }
            }
        );
    }
}
